import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchUserInfo, resetStatus } from "../redux/usersSagas";
import { MainModal } from "../../../../components/modal/MainModal";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import { ErrorList } from "../../../../components/ErrorList";
import { DataPermissions } from "./DataPermissions";
import { UserInfo } from "./UserInfo";
import { AccessPermissions } from "./AccessPermissions";
import { useError } from "../hooks/useError";
import { useEdit } from "../hooks/useEdit";
import { showToast } from "../../../../utils/showToast";
import {
  extendPermissionByCustom,
  rearrangeDailyEmailsPermissions
} from "../../../../utils/permissionsUtils";

export const Edit = ({
  isOpen,
  onClose,
  selected: { statuses: { isAdmin } = {}, id, orgName }
}) => {
  const dispatch = useDispatch();

  const {
    form: { register, control, setValue, watch, formState: { errors, dirtyFields } = {} } = {},
    submit,
    linkUsersToSharePermissions,
    canChangePermissions,
    watchedValues,
    orgRoles,
    currentUserInfo,
    allPermissions,
    organizations
  } = useEdit({ isAdmin, id, orgName });

  let {
    firstName,
    lastName,
    logs,
    userActions: {
      canChangeUserEmail,
      canChange2FA,
      canChangeRole,
      canChangeEmails,
      canEditLocations,
      canSendInactiveUsersEmail,
      canUpdateCorporateReports
    } = {}
  } = currentUserInfo || {};
  const [activeTab, setActiveTab] = useState(0);

  const close = () => {
    dispatch(resetStatus());
    onClose();
  };

  useEffect(() => {
    dispatch(fetchUserInfo(id));
  }, []);

  const { errors: serverErrors, status } = useError(() => {
    showToast("User was successfully updated");
    close();
  });

  const primaryButtonDisabled =
    (dirtyFields && Object.keys(dirtyFields).length === 0) || status == "pending";

  const activeTabClassName = "tabs__tab--active";

  let { permissions, daily } = rearrangeDailyEmailsPermissions(allPermissions);

  const renderPermissions = () => (
    <DataPermissions
      watch={watch}
      setValue={setValue}
      allPermissions={extendPermissionByCustom(permissions, orgName)}
      dailyEmailsPermissions={daily}
      control={control}
      register={register}
      canChangeEmails={canChangeEmails}
      canUpdateCorporateReports={canUpdateCorporateReports}
      canSendInactiveUsersEmail={canSendInactiveUsersEmail}
      canChangePermissions={canChangePermissions}
      orgRoles={orgRoles}
      canChangeRole={canChangeRole}
      watchedValues={watchedValues}
      linkUsersToSharePermissions={linkUsersToSharePermissions}
    />
  );

  const renderUserInfo = () => (
    <UserInfo
      register={register}
      control={control}
      canChange2FA={canChange2FA}
      canChangeUserEmail={canChangeUserEmail}
      logs={logs}
    />
  );

  const renderActiveTab = () => {
    if (activeTab === 0) {
      return renderUserInfo();
    } else if (activeTab == 1) {
      return renderPermissions();
    }

    return (
      <AccessPermissions
        isAdmin={isAdmin}
        watchedValues={watchedValues}
        register={register}
        control={control}
        setValue={setValue}
        canEditLocations={canEditLocations}
        canChangePermissions={canChangePermissions}
        allPermissions={extendPermissionByCustom(permissions, orgName)}
        organizations={organizations}
        linkUsersToSharePermissions={linkUsersToSharePermissions}
      />
    );
  };

  return (
    <>
      <MainModal
        isLoading={!currentUserInfo}
        parentSelector=".admin-users"
        isOpen={isOpen}
        onClose={close}
        headerText={
          <>
            Edit User -{" "}
            <span className="text-bold">
              {lastName}, {firstName}
            </span>
          </>
        }
        headerCloseHandler={close}
        height="auto"
        width={900}
        primaryButtonText="Save"
        primaryBtnHandler={submit}
        primaryButtonDisabled={primaryButtonDisabled}
        secondaryBtnText="Cancel"
        secondaryBtnHandler={close}
        containerClassName="edit"
      >
        {!isAdmin && (
          <div className="tabs">
            <div className="tabs__list">
              <button
                className={`tabs__tab ${activeTab === 0 ? activeTabClassName : ""}`}
                onClick={() => setActiveTab(0)}
              >
                User Info
              </button>
              <button
                className={`tabs__tab ${activeTab === 1 ? activeTabClassName : ""}`}
                onClick={() => setActiveTab(1)}
              >
                Data Permissions
              </button>
              <button
                className={`tabs__tab ${activeTab === 2 ? activeTabClassName : ""}`}
                onClick={() => setActiveTab(2)}
              >
                Access Permissions
              </button>
            </div>

            <div>{renderActiveTab()}</div>
          </div>
        )}
        {isAdmin && renderUserInfo()}
        {status === "pending" && <LoadingAnimation uiLoader />}

        <ErrorList errors={{ ...errors, ...serverErrors }} />
      </MainModal>
    </>
  );
};
